import { login } from "../api/accountManagement";
import { UserRole } from "../models/AccountManagement";
import sessionStorageClient from "./sessionStorageClient";

interface AuthFailureResult {
  isError: true;
  error: string;
}

interface AuthSuccessResult {
  isError: false;
  displayName: string;
  role: UserRole;
  firstName: string;
  lastName: string;
}

export type AuthResult = AuthFailureResult | AuthSuccessResult;

export type LogoutMessage = {
  isSuccess: boolean;
  message: string;
}

const createAuthClient = () => {
  const logIn = async (
    email: string,
    password: string
  ): Promise<AuthResult> => {
    const response = await login(email, password);

    if (response.isError) {
      return {
        isError: true,
        error: response.message,
      };
    }

    sessionStorageClient.setAccountInformation(response.content);
    sessionStorageClient.setToken(response.content.accessToken);

    return {
      isError: false,
      displayName: `${response.content.firstName} ${response.content.lastName}`,
      role: response.content.role,
      firstName: response.content.firstName,
      lastName: response.content.lastName,
    };
  };

  const logOut = async (message?: LogoutMessage): Promise<AuthResult> => {
    sessionStorageClient.clearAccountInformation();
    sessionStorageClient.clearToken();
    localStorage.clear();
    if (message) {
      localStorage.setItem('toastNotification', JSON.stringify(message));
    }

    return {
      isError: false,
      displayName: "",
      role: "",
      firstName: "",
      lastName: "",
    };
  };

  const getToken = () => {
    return sessionStorageClient.getToken();
  };

  const loadSession = () => {
    const accountInformation = sessionStorageClient.getAccountInformation();
    const userName = accountInformation?.email;
    const firstName = accountInformation?.firstName;
    const lastName = accountInformation?.lastName;
    const role = accountInformation?.role;

    return {
      isLoggedIn: !!userName,
      userName,
      firstName,
      lastName,
      role,
    };
  };

  const getAccountInformation = () => {
    return sessionStorageClient.getAccountInformation();
  };

  return {
    logIn,
    logOut,
    loadSession,
    getToken,
    getAccountInformation,
  };
};

const authClient = createAuthClient();

export default authClient;
